import React, { useState } from "react";
import { Dialog } from "../Dialog/Dialog";
import { BASE_URL } from "../../../constants/Constants";

type RevokeApplicationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: { reason: string; reasonCategory: string }) => void;
  applicationId?: string;
  applicantName?: string;
  isPending?: boolean;
};

const REVOCATION_CATEGORIES = [
  "Court Dispute Order Received",
  "Fraudulent Ownership Documents",
  "Unauthorized Construction / Layout Violation",
  "Administrative / Municipal Directive",
  "Incorrect Jurisdiction / Boundary Dispute",
  "Other",
];

export const RevokeApplicationModal: React.FC<RevokeApplicationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  applicationId,
  applicantName,
  isPending = false,
}) => {
  const [reasonCategory, setReasonCategory] = useState<string>(
    REVOCATION_CATEGORIES[0]
  );
  const [reason, setReason] = useState<string>("");
  const [error, setError] = useState<string>("");

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError("Please provide a reason for revocation.");
      return;
    }
    if (reason.trim().length < 10) {
      setError("Revocation reason must be at least 10 characters long.");
      return;
    }
    setError("");
    onConfirm({ reason: reason.trim(), reasonCategory });
  };

  return (
    <Dialog open={isOpen} onClose={isPending ? () => {} : onClose}>
      <div className="w-full max-w-lg p-6 bg-white rounded-2xl shadow-xl">
        <div className="flex items-center space-x-3 border-b pb-4 mb-4">
          <div className="p-2 bg-red-100 rounded-full text-red-600">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-bold text-red-600">
              Revoke Application
            </h3>
            <p className="text-xs text-gray-500">
              Permanent and Irreversible Action
            </p>
          </div>
        </div>

        {applicationId && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-900">
            <p>
              <span className="font-semibold">Application ID:</span>{" "}
              {applicationId}
            </p>
            {applicantName && (
              <p>
                <span className="font-semibold">Applicant:</span> {applicantName}
              </p>
            )}
            <p className="text-xs text-red-700 mt-1 font-medium">
              ⚠️ Warning: Revoking will permanently freeze this application. No
              resubmission, payment, or certificate issuance will be permitted.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Revocation Category <span className="text-red-500">*</span>
            </label>
            <select
              value={reasonCategory}
              onChange={(e) => setReasonCategory(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              disabled={isPending}
            >
              {REVOCATION_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Detailed Reason / Remarks <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={4}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (error) setError("");
              }}
              placeholder="Enter mandatory revocation reason (e.g., Court dispute order dated DD/MM/YYYY, fraudulent title deed detected...)"
              className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              disabled={isPending}
            />
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-gray-400">
                Minimum 10 characters ({reason.trim().length}/10)
              </span>
              {error && <span className="text-xs text-red-600 font-semibold">{error}</span>}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="px-5 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || reason.trim().length < 10}
              className="px-5 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:text-gray-500 rounded-lg transition"
            >
              {isPending ? "Revoking..." : "Confirm Revocation"}
            </button>
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default RevokeApplicationModal;

